// websocketAudioService.ts
class WebSocketAudioService {
  private ws: WebSocket | null = null
  private audioContext: AudioContext | null = null
  private currentAudioSource: AudioBufferSourceNode | null = null
  private heartBeatInterval: NodeJS.Timeout | null = null
  private currentPreviewId = 0
  private isPlaying = false
  private nextSequence = 0
  private audioQueue: Array<{ buffer: AudioBuffer; previewId: number; sequence: number }> = []
  private listeners: {
    statusChange?: (status: 'connecting' | 'connected' | 'disconnected') => void
    error?: (error: string) => void
    playbackEnd?: () => void
    playbacking?: () => void
  } = {}

  constructor(private wsUrl: string) {}

  // 初始化音频上下文
  private initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
  }

  // 停止当前播放
  private stopCurrentPlayback() {
    this.audioQueue = []
    this.isPlaying = false
    if (this.currentAudioSource) {
      try {
        this.currentAudioSource.stop()
      } catch (e) {
        // 可能已经自然结束，忽略错误
      }
      this.currentAudioSource = null
    }
  }

  private async processPCMAudio(data: Blob, previewId: number) {
    if (!this.audioContext || previewId !== this.currentPreviewId) return

    const currentSequence = this.nextSequence++ // 获取当前序列号

    try {
      const arrayBuffer: ArrayBuffer = await new Response(data).arrayBuffer()
      const wavData = encodePCMToWAV(arrayBuffer)

      const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
        this.audioContext?.decodeAudioData(
          wavData,
          (buffer) => resolve(buffer),
          (error) => reject(new Error(`解码失败: ${error.message}`))
        )
      })

      // 将解码后的音频加入队列，附带序列号
      this.audioQueue.push({ buffer: audioBuffer, previewId, sequence: currentSequence })

      // 按序列号排序队列
      this.audioQueue.sort((a, b) => a.sequence - b.sequence)

      if (!this.isPlaying) {
        this.playNextInQueue()
      }
    } catch (error: any) {
      console.error('PCM处理失败:', error)
      this.listeners.error?.(`音频处理错误: ${error?.message}`)
    }
  }

  private playNextInQueue() {
    if (!this.audioContext) return
    if (this.audioQueue.length === 0) {
      this.isPlaying = false
      this.listeners.playbackEnd?.()
      return
    }

    const nextAudio = this.audioQueue.shift()
    if (!nextAudio || nextAudio.previewId !== this.currentPreviewId) {
      this.playNextInQueue()
      return
    }

    if (!this.isPlaying) {
      this.listeners.playbacking?.()
    }

    this.isPlaying = true
    const source = this.audioContext.createBufferSource()
    source.buffer = nextAudio.buffer
    source.connect(this.audioContext.destination)
    source.start(0)

    source.onended = () => {
      this.playNextInQueue()
    }
  }

  // 建立WebSocket连接
  private connectWebSocket(callback?: () => void) {
    if (this.ws) return

    this.listeners.statusChange?.('connecting')
    const ws = new WebSocket(this.wsUrl)

    ws.onopen = () => {
      this.listeners.statusChange?.('connected')
      this.startHeartBeat()
      callback?.()
    }

    ws.onmessage = (event) => {
      if (event.data instanceof Blob) {
        this.processPCMAudio(event.data, this.currentPreviewId)
      } else if (typeof event.data === 'string') {
        console.log('WebSocket message:', event.data)
      }
    }

    ws.onclose = () => {
      this.listeners.statusChange?.('disconnected')
      this.stopHeartBeat()
      this.ws = null
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      this.listeners.error?.('WebSocket connection error')
      this.listeners.statusChange?.('disconnected')
    }

    this.ws = ws
  }

  // 心跳机制
  private startHeartBeat() {
    if (this.heartBeatInterval) return

    this.heartBeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'heartbeat' }))
      }
    }, 30000)
  }

  private stopHeartBeat() {
    if (this.heartBeatInterval) {
      clearInterval(this.heartBeatInterval)
      this.heartBeatInterval = null
    }
  }

  // 注册事件监听器
  public onStatusChange(callback: (status: 'connecting' | 'connected' | 'disconnected') => void) {
    this.listeners.statusChange = callback
  }

  public onError(callback: (error: string) => void) {
    this.listeners.error = callback
  }

  public onPlaybacking(callback: () => void) {
    this.listeners.playbacking = callback
  }

  public onPlaybackEnd(callback: () => void) {
    this.listeners.playbackEnd = callback
  }

  // 开始试听
  public startPreview(params?: any) {
    // 生成新的预览ID，用于标识当前预览请求
    this.currentPreviewId = Date.now()
    this.stopCurrentPlayback()

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connectWebSocket(() => this.sendPreview(params))
    } else if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendPreview(params)
    }
    this.initAudioContext()
  }

  public sendPreview(params?: any) {
    this.ws?.send(
      JSON.stringify({
        type: 'preview_request',
        ...params
      })
    )
  }

  // 停止试听
  public stopPreview() {
    this.stopCurrentPlayback()
  }

  // 清理资源
  public dispose() {
    this.stopPreview()
    this.stopHeartBeat()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }
}

// 全局单例
let audioServiceInstance: WebSocketAudioService | null = null

export const initAudioService = (wsUrl: string) => {
  if (audioServiceInstance) {
    audioServiceInstance.dispose()
  }
  audioServiceInstance = new WebSocketAudioService(wsUrl)
  return audioServiceInstance
}

export const getAudioService = () => {
  if (!audioServiceInstance) {
    throw new Error('Audio service not initialized. Call initAudioService first.')
  }
  return audioServiceInstance
}

// pcmToWav.ts
export function encodePCMToWAV(pcmData: ArrayBuffer, sampleRate = 16000, numChannels = 1, bitDepth = 16): ArrayBuffer {
  const bytesPerSample = bitDepth / 8
  const blockAlign = numChannels * bytesPerSample

  const wavHeader = new ArrayBuffer(44)
  const view = new DataView(wavHeader)

  // RIFF标识
  writeString(view, 0, 'RIFF')
  // 文件长度 (PCM数据长度 + 44 - 8)
  view.setUint32(4, 36 + pcmData.byteLength, true)
  // WAVE标识
  writeString(view, 8, 'WAVE')
  // fmt子块
  writeString(view, 12, 'fmt ')
  // fmt块长度
  view.setUint32(16, 16, true)
  // 编码方式 (1=PCM)
  view.setUint16(20, 1, true)
  // 声道数
  view.setUint16(22, numChannels, true)
  // 采样率
  view.setUint32(24, sampleRate, true)
  // 字节率
  view.setUint32(28, sampleRate * blockAlign, true)
  // 块对齐
  view.setUint16(32, blockAlign, true)
  // 位深度
  view.setUint16(34, bitDepth, true)
  // data标识
  writeString(view, 36, 'data')
  // data块长度
  view.setUint32(40, pcmData.byteLength, true)

  // 合并头部和PCM数据
  const wavData = new Uint8Array(44 + pcmData.byteLength)
  wavData.set(new Uint8Array(wavHeader), 0)
  wavData.set(new Uint8Array(pcmData), 44)

  return wavData.buffer
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}
